// Sidebar — left-rail navigation for the admin shell.
//
// Grouped per admin/.claude/agents/admin-frontend-engineer.md (Shell
// anatomy): Sales / Catalog / Comms / Future / System. Links use the
// brand designer's .gs-nav__link primitive; the active state is driven
// by TanStack Router's `activeProps`.
//
// Section behavior (v0.2):
//   • Each group label is now a <button> that toggles its items open/closed.
//   • Default-open: SALES (lands on /orders).
//   • Auto-open: any group whose item matches the current pathname.
//   • Persistence: per-user choice lives at
//     localStorage["goodsweat.admin.sidebar.openGroups"] = string[]
//     so a return visit restores the same expanded set, with the
//     auto-open-active rule still layered on top.
//   • Animation: CSS max-height (0 → 600px headroom) + opacity, 150ms ease-out.

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Banknote,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  Coins,
  CreditCard,
  History,
  Key,
  Mail,
  Megaphone,
  Package,
  RefreshCcw,
  Repeat,
  Settings,
  ShoppingCart,
  Sparkles,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  soon?: boolean;
}

interface NavGroup {
  /** Display label (already uppercased by .gs-nav__group-label). */
  label: string;
  /** Stable id used for localStorage + aria-controls. */
  id: string;
  items: NavItem[];
}

const groups: NavGroup[] = [
  {
    id: 'sales',
    label: 'Sales',
    items: [
      { to: '/orders', label: 'Orders', icon: ShoppingCart },
      { to: '/transactions', label: 'Transactions', icon: CreditCard },
      { to: '/refunds', label: 'Refunds', icon: RefreshCcw },
      { to: '/disputes', label: 'Disputes', icon: AlertTriangle },
      { to: '/payouts', label: 'Payouts', icon: Banknote },
      { to: '/subscriptions', label: 'Subscriptions', icon: Repeat },
      { to: '/customers', label: 'Customers', icon: Users },
      { to: '/coupons', label: 'Coupons', icon: Coins },
      { to: '/reports', label: 'Reports', icon: BarChart3 },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      { to: '/products', label: 'Products', icon: Package },
      { to: '/products/bundles', label: 'Bundles', icon: Sparkles },
    ],
  },
  {
    id: 'comms',
    label: 'Comms',
    items: [
      { to: '/influencers', label: 'Creators', icon: Sparkles },
      { to: '/emails/templates', label: 'Templates', icon: ClipboardList },
      { to: '/emails/sequences', label: 'Sequences', icon: Repeat },
      { to: '/emails/broadcasts', label: 'Broadcasts', icon: Megaphone },
      { to: '/emails/sends', label: 'Sends', icon: Mail },
    ],
  },
  {
    id: 'future',
    label: 'Future',
    items: [
      { to: '/shipping', label: 'Shipping', icon: Truck, soon: true },
      { to: '/production', label: 'Production', icon: Wrench, soon: true },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { to: '/audit', label: 'Audit log', icon: History },
      { to: '/settings/users', label: 'Users', icon: Users },
      { to: '/settings/channels', label: 'Channels', icon: CircleDollarSign },
      { to: '/settings/api-keys', label: 'API Keys', icon: Key },
      { to: '/settings/branding', label: 'Branding', icon: Settings },
    ],
  },
];

const STORAGE_KEY = 'goodsweat.admin.sidebar.openGroups';
/** Default-open set. Only SALES because Orders is the landing page. */
const DEFAULT_OPEN: ReadonlyArray<string> = ['sales'];

function readStoredOpen(): Set<string> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return null;
  }
}

function writeStoredOpen(open: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...open]));
  } catch {
    /* localStorage may be unavailable (private mode); silently noop. */
  }
}

/** Find which group, if any, contains the active pathname. Longest
 *  matching `to` wins so `/products/bundles` resolves to Bundles, not
 *  Products. */
function groupIdForPath(pathname: string): string | null {
  let bestId: string | null = null;
  let bestLen = -1;
  for (const g of groups) {
    for (const item of g.items) {
      if (
        (pathname === item.to ||
          pathname.startsWith(`${item.to}/`)) &&
        item.to.length > bestLen
      ) {
        bestLen = item.to.length;
        bestId = g.id;
      }
    }
  }
  return bestId;
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeGroupId = useMemo(() => groupIdForPath(pathname), [pathname]);

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const stored = readStoredOpen();
    return stored ?? new Set(DEFAULT_OPEN);
  });

  // Auto-expand the group containing the active route. We layer this on
  // top of the persisted set instead of replacing it, so the user's
  // other toggles survive.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) => {
      if (prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.add(activeGroupId);
      return next;
    });
  }, [activeGroupId]);

  // Persist every change. The active-route auto-expand goes through the
  // same setter so it gets persisted too — which is what we want.
  useEffect(() => {
    writeStoredOpen(openGroups);
  }, [openGroups]);

  function toggleGroup(id: string): void {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <aside
      className={cn(
        'flex flex-col h-full',
        'bg-[var(--color-surface-sunken)] border-r border-[var(--color-border)]',
      )}
      style={{ width: 'var(--sidebar-w, 240px)' }}
    >
      <div className="px-4 py-5">
        <Link
          to="/orders"
          className="block font-display font-semibold leading-none"
          style={{
            fontSize: 20,
            letterSpacing: '-0.005em',
            color: 'var(--color-forest-700)',
          }}
        >
          GoodSweat
          <span
            className="ml-1 align-middle"
            style={{
              fontSize: 11,
              color: 'var(--color-fg-subtle)',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            admin
          </span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto scroll-warm pb-6">
        {groups.map((group) => {
          const isOpen = openGroups.has(group.id);
          const panelId = `gs-nav-group-${group.id}`;
          return (
            <div key={group.id}>
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="gs-nav__group-toggle"
              >
                <span className="gs-nav__group-label-inline">{group.label}</span>
                {isOpen ? (
                  <ChevronDown size={12} aria-hidden="true" />
                ) : (
                  <ChevronRight size={12} aria-hidden="true" />
                )}
              </button>
              <div
                id={panelId}
                className={cn('gs-nav__group-panel', isOpen && 'is-open')}
                aria-hidden={!isOpen}
              >
                <div className="gs-nav">
                  {group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={onNavigate}
                      className="gs-nav__link"
                      activeProps={{ className: 'gs-nav__link is-active' }}
                      activeOptions={{ exact: false }}
                      // When collapsed the panel has max-height: 0 +
                      // visibility: hidden via CSS, but TanStack still
                      // tab-orders these links. tabIndex={-1} keeps
                      // keyboard focus out of a hidden region.
                      tabIndex={isOpen ? 0 : -1}
                    >
                      <item.icon size={16} aria-hidden="true" />
                      <span className="flex-1">{item.label}</span>
                      {item.soon ? (
                        <Badge variant="muted" className="ml-2 tracking-wider">
                          Soon
                        </Badge>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div
        className="px-4 py-3 border-t border-[var(--color-border)]"
        style={{ color: 'var(--color-fg-subtle)', fontSize: 11 }}
      >
        <span className="font-display tracking-[0.16em] uppercase">GoodSweat</span>{' '}
        admin v0.1.0
      </div>
    </aside>
  );
}
